import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useMe } from "../auth/queries";
import { useI18n } from "../i18n/I18nProvider";
import { rememberWorkspace, readRecentWorkspaces } from "../lib/recent-workspaces";
import { orderMemberships } from "../features/workspace/order-memberships";
import { useWorkspaceMemberships } from "../features/workspace/queries";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { LabelGrid, LabelGridRow } from "../components/ui/LabelGrid";
import { PageHeader } from "../components/ui/PageHeader";
import { ACTION_LINK_CLASS } from "../components/ui/action-link";
import { Panel } from "../components/ui/feedback";
import type { MessageKey } from "../i18n/en";

const ROLE_KEYS = {
  owner: "common.role.owner",
  member: "common.role.member",
} as const satisfies Record<"owner" | "member", MessageKey>;

const SECONDARY_LINK_CLASS =
  "inline-flex min-h-11 items-center justify-center rounded-control border border-edge px-4 font-semibold text-ink transition-colors duration-[120ms] hover:bg-board";

/**
 * /app: the workspace picker. Rows are the operator's live server memberships, recent-first.
 * A single workspace still shows the picker, with its Open as the primary action. The ID form
 * opens any other workspace by id.
 */
export function AppIndexPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const me = useMe();
  const memberships = useWorkspaceMemberships();
  const [workspaceId, setWorkspaceId] = useState("");
  const [recent] = useState(readRecentWorkspaces);
  const rows = orderMemberships(memberships.data ?? [], recent);
  const isPrimary = rows.length === 1;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const id = workspaceId.trim();
    if (id) {
      void navigate({ to: "/app/$workspaceId/roster", params: { workspaceId: id } });
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        title={
          rows.length === 0 && memberships.isSuccess ? t("workspace.none") : t("workspace.yours")
        }
      />
      {memberships.isPending ? <p className="text-operate text-ink-2">{t("app.loading")}</p> : null}
      {memberships.isError ? (
        <div className="space-y-3">
          <p role="alert" className="text-operate text-ink-2">
            {t("error.unknown")}
          </p>
          <Button variant="secondary" onClick={() => void memberships.refetch()}>
            {t("app.retry")}
          </Button>
        </div>
      ) : null}
      {memberships.isSuccess && rows.length === 0 ? (
        <EmptyState>{t("workspace.noneText")}</EmptyState>
      ) : null}
      {rows.length > 0 ? (
        <LabelGrid>
          {rows.map(({ id, name, role }) => (
            <LabelGridRow key={id} columns="minmax(0,1fr) auto auto">
              <span
                className="min-w-0 truncate font-display text-xl leading-tight font-bold tracking-[0.01em] text-ink uppercase"
                title={name}
              >
                {name}
              </span>
              <span className="text-note text-ink-2">{t(ROLE_KEYS[role])}</span>
              <Link
                to="/app/$workspaceId/roster"
                params={{ workspaceId: id }}
                onClick={() => rememberWorkspace(id)}
                className={isPrimary ? ACTION_LINK_CLASS : SECONDARY_LINK_CLASS}
              >
                {t("workspace.openRow")}
              </Link>
            </LabelGridRow>
          ))}
        </LabelGrid>
      ) : null}
      {me.data?.isAdmin ? (
        <Link to="/admin" className={SECONDARY_LINK_CLASS}>
          {t("admin.nav")}
        </Link>
      ) : null}
      <Panel className="max-w-md space-y-3">
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block space-y-1 text-operate">
            <span>{t("workspace.idLabel")}</span>
            <input
              value={workspaceId}
              onChange={(event) => setWorkspaceId(event.target.value)}
              className="h-11 w-full font-mono"
              required
            />
          </label>
          <Button type="submit" variant="primary" className="w-full">
            {t("workspace.open")}
          </Button>
        </form>
      </Panel>
    </div>
  );
}
